import { plural } from '@ipfabric/frontend-shared-assets/utils/plural';
import { Badge } from '@ipfabric/ui-components/Badge/Badge';
import { Button } from '@ipfabric/ui-components/Button/Button';
import type { IconButtonProps } from '@ipfabric/ui-components/IconButton/IconButton.types';
import { LeaveDialog } from '@ipfabric/ui-components/LeaveDialog/LeaveDialog';
import { Link } from '@ipfabric/ui-components/Link/Link';
import { ListDivider } from '@ipfabric/ui-components/List/ListDivider/ListDivider';
import { MenuItem } from '@ipfabric/ui-components/MenuItem/MenuItem';
import { MenuItemsColumn } from '@ipfabric/ui-components/MenuItem/MenuItemsColumn';
import { PageHeader } from '@ipfabric/ui-components/PageHeader/PageHeader';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import { useFormComposition } from '@ipfabric/ui-components/utils/formSnippets/useFormComposition';
import type { Awaitable } from '@ipfabric/ui-components/utils/types/Awaitable';
import { type SnapshotId, safeAbsurd } from '@ipfabric/utils';
import { triggerAnalyticsEvent } from 'app/analytics/posthogEvents';
import classNames from 'classnames';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useLicenseHasValidAim } from 'utils/hooks/useLicenseHasValidAim';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useAimOrphanDetection } from '../../components/ApplicationMapping/tables/helpers/useAimOrphanDetection';
import { ApplicationMappingTab } from '../../components/ApplicationMapping/tabs/ApplicationMappingTab';
import { AuthenticationValidationContext } from '../../components/Authentication/context/AuthenticationValidationContext';
import { useAuthenticationValidation } from '../../components/Authentication/hooks/useAuthenticationValidation';
import {
  type AuthenticationSubTab,
  AuthenticationTab,
} from '../../components/Authentication/tabs/AuthenticationTab';
import { DeviceAttributes } from '../../components/DeviceAttributes/DeviceAttributes';
import { useDeviceAttributesCount } from '../../components/DeviceAttributes/hooks/useDeviceAttributesCount';
import { DiscoverySearch } from '../../components/DiscoverySearch/DiscoverySearch';
import { SearchContextWrapper } from '../../components/DiscoverySearch/SearchContext';
import { extractGroupSearchItems } from '../../components/DiscoverySearch/utils/extractGroupSearchItems';
import {
  type ResponseVendor,
  syncVendorIdsFromResponse,
} from '../../components/VendorsApi/helpers/syncVendorIdsFromResponse';
import type { DiscoverySettingsObject } from '../../model/DiscoverySettingsObject';
import { customDiscoveryDataMenuItems } from './customDiscoveryDataMenuItems';
import { DiscoverySettingsContext } from './discoverySettingsContext';
import { advancedCliGroup } from './groups/advancedCliGroup';
import { aimApplicationsGroup } from './groups/aimApplicationsGroup';
import { aimFlowsGroup } from './groups/aimFlowsGroup';
import { aimIntegrationsGroup } from './groups/aimIntegrationsGroup';
import { aimWorkloadsGroup } from './groups/aimWorkloadsGroup';
import { assuranceEngineGroup } from './groups/assuranceEngineGroup';
import { bgpCommunitiesGroup } from './groups/bgpCommunitiesGroup';
import { customDiscoveryDefinitionsGroup } from './groups/customDiscoveryDefinitionsGroup';
import { deviceCredentialsGroup } from './groups/deviceCredentialsGroup';
import { disabledDiscoveryTasksGroup } from './groups/disabledDiscoveryTasksGroup';
import { discoveryGroup } from './groups/discoveryGroup';
import { manualDeviceLinksGroup } from './groups/manualDeviceLinksGroup';
import { privilegedCredentialsGroup } from './groups/privilegedCredentialsGroup';
import { siteSeparationGroup } from './groups/siteSeparationGroup';
import { vendorsApiGroup } from './groups/vendorsApi';

type BareProps = {
  className?: string;
  initialModel: DiscoverySettingsObject;
  onBackButtonClick?: () => void;
  onSubmit: (model: DiscoverySettingsObject) => Awaitable<unknown>;
  pageTitle?: string;
  saveButtonDisabled?: boolean;
  saveButtonLabel?: string;
  saveButtonIcon?: IconButtonProps['icon'] | null;
  snapshotId: SnapshotId | null;
};

type Props = BareProps & {
  newSnapshot?: boolean;
  snapshotId: SnapshotId | null;
  showUpdateFromGlobalSettingsButton: boolean;
};

type DiscoverySettingsTab =
  | 'routing'
  | 'discovery'
  | 'disabledDiscoveryTasks'
  | 'advancedCli'
  | 'customDiscoveryData'
  | 'siteSeparation'
  | 'assuranceEngine'
  | 'authentication'
  | 'manualDeviceLinks'
  | 'deviceAttributes'
  | 'applicationMapping';

export type ApplicationMappingSubTab =
  | 'aimApplications'
  | 'aimWorkloads'
  | 'aimFlows'
  | 'aimIntegrations';

export type DiscoverySettingsGroupDisplayControl = {
  setTab: (tab: DiscoverySettingsTab) => void;
  setAuthenticationSubTab: (subTab: AuthenticationSubTab) => void;
  setApplicationMappingSubTab: (subTab: ApplicationMappingSubTab) => void;
};

const groups = {
  aimApplications: aimApplicationsGroup,
  aimFlows: aimFlowsGroup,
  aimIntegrations: aimIntegrationsGroup,
  aimWorkloads: aimWorkloadsGroup,
  bgpCommunities: bgpCommunitiesGroup,
  customDiscoveryDefinitions: customDiscoveryDefinitionsGroup,
  discovery: discoveryGroup,
  advancedCli: advancedCliGroup,
  siteSeparation: siteSeparationGroup,
  disabledDiscoveryTasks: disabledDiscoveryTasksGroup,
  assuranceEngine: assuranceEngineGroup,
  deviceCredentials: deviceCredentialsGroup,
  privilegedCredentials: privilegedCredentialsGroup,
  manualDeviceLinks: manualDeviceLinksGroup,
  vendors: vendorsApiGroup,
};

const BareDiscoverySettings = (props: BareProps) => {
  const [tab, setTab] = useState<DiscoverySettingsTab>('discovery');
  const [selectedSearchId, setSelectedSearchId] = useState<
    string | undefined
  >();
  const [authenticationSubTab, setAuthenticationSubTab] =
    useState<AuthenticationSubTab>('deviceCredentials');
  const [applicationMappingSubTab, setApplicationMappingSubTab] =
    useState<ApplicationMappingSubTab>('aimIntegrations');

  const confirmIncompleteAuthenticationSettingsDialog =
    useSimpleConfirmDialog();

  const setTabWithAnalytics = useStableCallback(
    (newTab: DiscoverySettingsTab) => {
      setTab((previousTab) => {
        if (previousTab !== newTab) {
          triggerAnalyticsEventOnTabChange(newTab, previousTab);
        }
        return newTab;
      });
    },
  );

  const groupDisplayControl: DiscoverySettingsGroupDisplayControl = useMemo(
    () => ({
      setTab: setTabWithAnalytics,
      setAuthenticationSubTab,
      setApplicationMappingSubTab,
    }),
    [],
  );

  const onSubmit = useStableCallback(async (model: DiscoverySettingsObject) => {
    const response = (await props.onSubmit(model)) as
      | { vendorApi?: ResponseVendor[] }
      | undefined;
    const responseVendors = response?.vendorApi;
    if (!Array.isArray(responseVendors)) {
      return;
    }
    // Sync FE vendor ids with BE-assigned _keys; see syncVendorIdsFromResponse.
    formComposition.form.setValue(
      'vendors',
      syncVendorIdsFromResponse(
        formComposition.form.getValues('vendors'),
        responseVendors,
      ),
      { shouldDirty: false },
    );
  });

  const formComposition = useFormComposition({
    groups,
    initialModel: props.initialModel,
    onSubmit,
    groupDisplayControl,
  });

  const form = formComposition.form;

  const siteSeparationRuleCount = useWatch({
    control: form.control,
    name: 'siteSeparation.rules',
  }).length;
  const networkIncludeRuleCount = useWatch({
    control: form.control,
    name: 'ipScopeInclude',
  }).ips.length;
  const networkExcludeRuleCount = useWatch({
    control: form.control,
    name: 'ipScopeExclude',
  }).ips.length;
  const discoverySeedsCount = useWatch({
    control: form.control,
    name: 'ipSeedList',
  }).ips.length;
  const bgpCommunitiesCount = useWatch({
    control: form.control,
    name: 'bgps',
  }).length;
  const disabledDiscoveryTasksCount = useWatch({
    control: form.control,
    name: 'disabledDiscoveryTasks',
  }).length;
  const customDiscoveryDefinitions = useWatch({
    control: form.control,
    name: 'customDiscoveryDefinitions',
  });
  const customDiscoveryDataEnabled = isFeatureFlagEnabled(
    'ENABLE_CUSTOM_DISCOVERY_DATA',
  );
  const graphCache = useWatch({ control: form.control, name: 'graphCache' })
    ? 1
    : 0;
  const historicalData = useWatch({
    control: form.control,
    name: 'historicalData',
  })
    ? 1
    : 0;
  const intentVerification = useWatch({
    control: form.control,
    name: 'intentVerification',
  })
    ? 1
    : 0;
  const disabledPostDiscoveryActionsCount =
    graphCache + historicalData + intentVerification;
  const manualDeviceLinksCount = useWatch({
    control: form.control,
    name: 'manualLinks',
  }).length;
  const customPortsCount = useWatch({
    control: form.control,
    name: 'customPorts',
  }).length;
  const storedDeviceCredentials = useWatch({
    control: form.control,
    name: 'credentials',
  });
  const storedPrivilegedCredentials = useWatch({
    control: form.control,
    name: 'privileges',
  });
  const storedVendors = useWatch({
    control: form.control,
    name: 'vendors',
  });
  const aimEnabled = useLicenseHasValidAim();
  const aimApplicationsCount = useWatch({
    control: form.control,
    name: 'aimApplications',
  }).length;
  const aimIntegrationsCount = useWatch({
    control: form.control,
    name: 'aimIntegrations',
  }).length;
  const deviceCredentialsCount = storedDeviceCredentials.length;
  const privilegedCredentialsCount = storedPrivilegedCredentials.length;
  const vendorsCount = storedVendors.length;

  const { count: attrCount, fetchCount: fetchAttrCount } =
    useDeviceAttributesCount(props.snapshotId);

  const authenticationValidation = useAuthenticationValidation(
    storedDeviceCredentials,
    storedVendors,
  );

  const notifications = useNotifications();

  const orphanDetection = useAimOrphanDetection(formComposition.form as never);

  const submitIfNoAimOrphans = useStableCallback(() => {
    if (orphanDetection.totalOrphans > 0) {
      notifications.showNotification({
        type: 'danger',
        message: `Cannot save: ${orphanDetection.totalOrphans} records have unresolved references. Fix or delete those records to save.`,
      });
      return;
    }
    formComposition.handleSubmit();
  });

  const handleSubmitWithValidation = useStableCallback(async () => {
    if (authenticationValidation.hasError) {
      await confirmIncompleteAuthenticationSettingsDialog.show(
        {
          header: 'Missing authentication',
          subHeader: (
            <>
              There are no active{' '}
              <Link
                renderAs='button'
                onClick={() => {
                  confirmIncompleteAuthenticationSettingsDialog.close();
                  setTabWithAnalytics('authentication');
                  setAuthenticationSubTab('deviceCredentials');
                }}
              >
                Device credential
              </Link>{' '}
              or{' '}
              <Link
                renderAs='button'
                onClick={() => {
                  confirmIncompleteAuthenticationSettingsDialog.close();
                  setTabWithAnalytics('authentication');
                  setAuthenticationSubTab('vendors');
                }}
              >
                Vendor API
              </Link>
              . These are required to run any discovery. Before continuing,
              please visit the Authentication section to resolve this issue.
            </>
          ),
          btnConfirm: 'Go to Authentication',
          btnSecondaryConfirm: 'Save anyway',
        },
        {
          btnConfirmAccent: 'positive',
          btnCancelAccent: 'positive',
          onClickConfirm() {
            setTabWithAnalytics('authentication');
            setAuthenticationSubTab('deviceCredentials');
            confirmIncompleteAuthenticationSettingsDialog.close();
          },
          onClickSecondaryConfirm() {
            confirmIncompleteAuthenticationSettingsDialog.close();
            submitIfNoAimOrphans();
          },
        },
      );
    } else if (authenticationValidation.hasWarning) {
      await confirmIncompleteAuthenticationSettingsDialog.show(
        {
          header: 'Expired credentials',
          subHeader:
            'Expired credentials may affect the discovery process. We recommend checking the Authentication section for potential issues.',
          btnConfirm: 'Go to Device Credentials',
          btnSecondaryConfirm: 'Save anyway',
        },
        {
          btnConfirmAccent: 'positive',
          btnCancelAccent: 'positive',
          onClickConfirm() {
            setTabWithAnalytics('authentication');
            setAuthenticationSubTab('deviceCredentials');
            confirmIncompleteAuthenticationSettingsDialog.close();
          },
          onClickSecondaryConfirm() {
            confirmIncompleteAuthenticationSettingsDialog.close();
            submitIfNoAimOrphans();
          },
        },
      );
    } else {
      submitIfNoAimOrphans();
    }
  });

  const menuItems = [
    <MenuItem
      label='Discovery'
      key='discovery'
      description={`${networkIncludeRuleCount} included / ${networkExcludeRuleCount} excluded / ${discoverySeedsCount} ${plural(discoverySeedsCount, 'seed')}`}
      selected={tab === 'discovery'}
      onClick={() => setTabWithAnalytics('discovery')}
    />,
    <MenuItem
      label='Authentication'
      key='authentication'
      description={`${deviceCredentialsCount} credentials / ${privilegedCredentialsCount} passwords / ${vendorsCount} Vendor APIs`}
      renderBadge={
        authenticationValidation.hasError
          ? () => (
              <Tooltip content={authenticationValidation.errorLong}>
                <Badge color='error' variant='plain' className='cursor-default'>
                  1 Error
                </Badge>
              </Tooltip>
            )
          : authenticationValidation.hasWarning
            ? () => (
                <Tooltip content={authenticationValidation.warningLong}>
                  <Badge
                    color='warning'
                    variant='plain'
                    className='cursor-default'
                  >
                    1 Issue
                  </Badge>
                </Tooltip>
              )
            : undefined
      }
      selected={tab === 'authentication'}
      onClick={() => setTabWithAnalytics('authentication')}
    />,
    <MenuItem
      label='Disabled discovery tasks'
      key='disabledDiscoveryTasks'
      description={`${disabledDiscoveryTasksCount} active ${plural(disabledDiscoveryTasksCount, 'rule')}`}
      selected={tab === 'disabledDiscoveryTasks'}
      onClick={() => setTabWithAnalytics('disabledDiscoveryTasks')}
    />,
    ...customDiscoveryDataMenuItems({
      definitions: customDiscoveryDefinitions,
      selected: tab === 'customDiscoveryData',
      onClick: () => setTabWithAnalytics('customDiscoveryData'),
    }),
    <MenuItem
      label='Site Separation'
      key='siteSeparation'
      description={`${siteSeparationRuleCount} ${plural(siteSeparationRuleCount, 'rule')}`}
      selected={tab === 'siteSeparation'}
      onClick={() => setTabWithAnalytics('siteSeparation')}
    />,
    <MenuItem
      label='Routing'
      key='routing'
      description={`${bgpCommunitiesCount} BGP ${plural(bgpCommunitiesCount, 'community', 'communities')}`}
      selected={tab === 'routing'}
      onClick={() => setTabWithAnalytics('routing')}
    />,
    ...(aimEnabled
      ? [
          <MenuItem
            label='Application mapping'
            key='applicationMapping'
            description={`${aimIntegrationsCount} ${plural(aimIntegrationsCount, 'integration')} / ${aimApplicationsCount} ${plural(aimApplicationsCount, 'manual application')}`}
            selected={tab === 'applicationMapping'}
            onClick={() => setTabWithAnalytics('applicationMapping')}
          />,
        ]
      : []),
    ...(props.snapshotId
      ? [
          <MenuItem
            label='Device attributes'
            key='deviceAttributes'
            description={`${attrCount ?? '?'} ${plural(attrCount ?? 0, 'attribute')}`}
            selected={tab === 'deviceAttributes'}
            onClick={() => setTabWithAnalytics('deviceAttributes')}
          />,
        ]
      : []),
    ...(isFeatureFlagEnabled('ENABLE_MANUAL_LINKS')
      ? [
          <MenuItem
            label='Manual device links'
            key='manualDeviceLinks'
            description={`${manualDeviceLinksCount} ${plural(manualDeviceLinksCount, 'link')}`}
            selected={tab === 'manualDeviceLinks'}
            onClick={() => setTabWithAnalytics('manualDeviceLinks')}
          />,
        ]
      : []),
    <MenuItem
      label='Advanced CLI'
      key='advancedCli'
      description={`${customPortsCount} custom ports`}
      selected={tab === 'advancedCli'}
      onClick={() => setTabWithAnalytics('advancedCli')}
    />,
    <MenuItem
      label='Assurance engine'
      key='assuranceEngine'
      description={
        disabledPostDiscoveryActionsCount === 3
          ? 'All options active'
          : `${disabledPostDiscoveryActionsCount} active ${plural(disabledPostDiscoveryActionsCount, 'option')}`
      }
      selected={tab === 'assuranceEngine'}
      onClick={() => setTabWithAnalytics('assuranceEngine')}
    />,
  ];

  // Extract searchable content from groups
  const groupSearchItems = useMemo(() => {
    const items = [];

    // Extract from discovery group
    items.push(
      ...extractGroupSearchItems(
        'discovery',
        'discovery',
        formComposition.renderGroup('discovery'),
      ),
    );

    // Extract from routing/bgpCommunities group
    items.push(
      ...extractGroupSearchItems(
        'bgpCommunities',
        'routing',
        formComposition.renderGroup('bgpCommunities'),
      ),
    );

    // Extract from other groups
    items.push(
      ...extractGroupSearchItems(
        'advancedCli',
        'advancedCli',
        formComposition.renderGroup('advancedCli'),
      ),
    );
    items.push(
      ...extractGroupSearchItems(
        'siteSeparation',
        'siteSeparation',
        formComposition.renderGroup('siteSeparation'),
      ),
    );
    items.push(
      ...extractGroupSearchItems(
        'disabledDiscoveryTasks',
        'disabledDiscoveryTasks',
        formComposition.renderGroup('disabledDiscoveryTasks'),
      ),
    );
    if (customDiscoveryDataEnabled) {
      items.push(
        ...extractGroupSearchItems(
          'customDiscoveryDefinitions',
          'customDiscoveryData',
          formComposition.renderGroup('customDiscoveryDefinitions'),
        ),
      );
    }
    items.push(
      ...extractGroupSearchItems(
        'assuranceEngine',
        'assuranceEngine',
        formComposition.renderGroup('assuranceEngine'),
      ),
    );
    items.push(
      ...extractGroupSearchItems(
        'deviceAttributes',
        'deviceAttributes',
        <DeviceAttributes fetchCount={() => {}} />,
      ),
    );

    // Extract from authentication groups
    const authPath = ['Authentication'];
    items.push(
      ...extractGroupSearchItems(
        'deviceCredentials',
        'authentication',
        formComposition.renderGroup('deviceCredentials'),
      ).map((item) => ({
        ...item,
        subTabName: 'deviceCredentials' as const,
        path: [...authPath, ...(item.path ?? [])],
      })),
    );
    items.push(
      ...extractGroupSearchItems(
        'vendors',
        'authentication',
        formComposition.renderGroup('vendors'),
      ).map((item) => ({
        ...item,
        subTabName: 'vendors' as const,
        path: [...authPath, ...(item.path ?? [])],
      })),
    );
    items.push(
      ...extractGroupSearchItems(
        'privilegedCredentials',
        'authentication',
        formComposition.renderGroup('privilegedCredentials'),
      ).map((item) => ({
        ...item,
        subTabName: 'privilegedCredentials' as const,
        path: [...authPath, ...(item.path ?? [])],
      })),
    );

    // AIM groups — gated by the AIM license so search cannot navigate to a hidden tab
    if (aimEnabled) {
      const aimPath = ['Application mapping'];
      items.push(
        ...extractGroupSearchItems(
          'aimApplications',
          'applicationMapping',
          formComposition.renderGroup('aimApplications'),
        ).map((item) => ({
          ...item,
          subTabName: 'aimApplications' as const,
          path: [...aimPath, ...(item.path ?? [])],
        })),
      );
      items.push(
        ...extractGroupSearchItems(
          'aimFlows',
          'applicationMapping',
          formComposition.renderGroup('aimFlows'),
        ).map((item) => ({
          ...item,
          subTabName: 'aimFlows' as const,
          path: [...aimPath, ...(item.path ?? [])],
        })),
      );
      items.push(
        ...extractGroupSearchItems(
          'aimWorkloads',
          'applicationMapping',
          formComposition.renderGroup('aimWorkloads'),
        ).map((item) => ({
          ...item,
          subTabName: 'aimWorkloads' as const,
          path: [...aimPath, ...(item.path ?? [])],
        })),
      );
    }

    return items;
  }, [formComposition, aimEnabled, customDiscoveryDataEnabled]);

  return (
    <>
      {confirmIncompleteAuthenticationSettingsDialog.render()}
      <LeaveDialog dirty={formComposition.form.formState.isDirty} />
      <PageHeader
        elevated
        width='full'
        label={props.pageTitle ?? 'Discovery settings'}
        buttons={
          <Button
            dataTestId='discovery-settings-save-button'
            disabled={props.saveButtonDisabled}
            icon={props.saveButtonIcon ?? 'fa-save'}
            loading={formComposition.form.formState.isSubmitting}
            onClick={handleSubmitWithValidation}
            variant='primary'
          >
            {props.saveButtonLabel ?? 'Save changes'}
          </Button>
        }
        onBackButtonClick={props.onBackButtonClick}
      />
      <div className='flex-1 m flex flex-col'>
        <div
          className={classNames(
            'flex-1 flex flex-row gap items-stretch',
            props.className,
          )}
        >
          <div>
            <DiscoverySearch
              menuItemElements={menuItems}
              groupSearchItems={groupSearchItems}
              onGroupSelect={(
                tabName: string,
                sectionId?: string,
                subTabName?: string,
              ) => {
                setSelectedSearchId(sectionId ?? tabName);
                if (tabName === 'authentication' && subTabName) {
                  setTabWithAnalytics('authentication');
                  setAuthenticationSubTab(subTabName as AuthenticationSubTab);
                } else if (tabName === 'applicationMapping' && subTabName) {
                  setTabWithAnalytics('applicationMapping');
                  setApplicationMappingSubTab(
                    subTabName as ApplicationMappingSubTab,
                  );
                } else {
                  setTabWithAnalytics(tabName as DiscoverySettingsTab);
                }
              }}
            />
            <ListDivider className='mt-4 mb-4' />
            <MenuItemsColumn className='flex-[0_0_268px]'>
              {menuItems}
            </MenuItemsColumn>
          </div>
          <div className='flex-1 flex flex-col'>
            <SearchContextWrapper
              selectedId={selectedSearchId}
              setSelectedId={setSelectedSearchId}
            >
              {(() => {
                switch (tab) {
                  case 'advancedCli':
                  case 'discovery':
                  case 'disabledDiscoveryTasks':
                  case 'siteSeparation':
                  case 'assuranceEngine':
                  case 'manualDeviceLinks':
                    return formComposition.renderGroup(tab);
                  case 'routing':
                    return formComposition.renderGroup('bgpCommunities');
                  case 'customDiscoveryData':
                    return formComposition.renderGroup(
                      'customDiscoveryDefinitions',
                    );
                  case 'authentication':
                    return (
                      <div className='flex flex-col flex-1 gap'>
                        <div className='text-default text-level-4 font-semibold font-heading'>
                          Authentication
                        </div>
                        <AuthenticationValidationContext.Provider
                          value={{
                            ...authenticationValidation,
                            setSubTab: setAuthenticationSubTab,
                          }}
                        >
                          <AuthenticationTab
                            formComposition={formComposition}
                            subTab={authenticationSubTab}
                            setSubTab={setAuthenticationSubTab}
                          />
                        </AuthenticationValidationContext.Provider>
                      </div>
                    );
                  case 'deviceAttributes':
                    return <DeviceAttributes fetchCount={fetchAttrCount} />;
                  case 'applicationMapping':
                    return (
                      <div className='flex flex-col flex-1 gap'>
                        <div className='text-default text-level-4 font-semibold font-heading'>
                          Application mapping
                        </div>
                        <ApplicationMappingTab
                          formComposition={formComposition}
                          subTab={applicationMappingSubTab}
                          setSubTab={setApplicationMappingSubTab}
                        />
                      </div>
                    );
                  default:
                    safeAbsurd(tab);
                    return <p>Error: unknown tab</p>;
                }
              })()}
            </SearchContextWrapper>
          </div>
        </div>
      </div>
    </>
  );
};

export const DiscoverySettings = ({
  newSnapshot = false,
  showUpdateFromGlobalSettingsButton = false,
  snapshotId,
  ...bareDiscoverySettingsProps
}: Props) => (
  <DiscoverySettingsContext.Provider
    value={{
      newSnapshot,
      snapshotId,
      showUpdateFromGlobalSettingsButton,
    }}
  >
    <BareDiscoverySettings
      {...bareDiscoverySettingsProps}
      snapshotId={snapshotId}
    />
  </DiscoverySettingsContext.Provider>
);

/* Analytics events */
const triggerAnalyticsEventOnTabChange = (
  newTab: string,
  previousTab: string,
) => {
  triggerAnalyticsEvent({
    eventName: 'navigation',
    data: {
      type: 'tab_change',
      previous_location: previousTab,
      new_location: newTab,
      element: 'tab',
      trigged_by: 'mouse_click',
    },
  });
};
